/**
 * The transport-agnostic resumable-upload driver.
 *
 * One bounded retry loop over both chunk writes and offset probes. The probe is
 * an operation of the same loop, subject to the same attempt budget, backoff,
 * session-gone branch and cancellation, so a failing probe needs no control
 * flow of its own.
 *
 * The driver speaks only {@link ChunkRequest}/{@link ChunkResponse} through a
 * {@link ChunkTransport} (XHR by default), and that seam carries progress and
 * cancellation too. The committed offset is **only ever** advanced from a
 * server response, never from a local counter, which is what keeps a
 * lost-response chunk from corrupting the object rather than merely re-sending
 * it.
 */
import {
  UPLOAD_CHUNK_MAX_ATTEMPTS,
  UPLOAD_CHUNK_SIZE_BYTES,
  UPLOAD_CHUNK_TIMEOUT_MS,
  UPLOAD_RETRY_BASE_DELAY_MS,
  UPLOAD_RETRY_MAX_DELAY_MS,
  type UploadTransportReason,
} from "@reflect/domain";

import type { ChunkTransport, SleepFn } from "./types";
import { xhrTransport } from "./xhrTransport";

/**
 * Exponential backoff for retry attempt `attempt` (1-based): the initial send
 * is attempt 0, so the first retry sleeps `BASE`, the next `2·BASE`, … capped at
 * {@link UPLOAD_RETRY_MAX_DELAY_MS}. `delay = min(BASE·2^(attempt-1), MAX)`.
 *
 * @param attempt - the incremented attempt count, ≥ 1.
 */
export function computeBackoffDelayMs(attempt: number): number {
  const raw = UPLOAD_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
  return Math.min(raw, UPLOAD_RETRY_MAX_DELAY_MS);
}

/** Throw the signal's abort reason if it has already fired. */
export function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw signal.reason;
  }
}

/**
 * The default {@link SleepFn}: resolves after `ms`, or rejects with the
 * signal's reason the moment it fires, so a pending backoff never keeps an
 * aborted run alive and an aborted loop stops instead of making one more call.
 *
 * @param ms - how long to wait.
 * @param signal - cancels the wait; omit it for a plain timer.
 */
export function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/** The non-terminal phases the driver reports as it works. */
export interface DriverPhaseUpdate {
  /** `uploading` while writing a chunk, `resuming` while probing/backing off. */
  phase: "uploading" | "resuming";
  /** Server-acknowledged high-water offset. */
  bytesCommitted: number;
  /** The recording's total byte length. */
  totalBytes: number;
  /** Consecutive failed operations without forward progress. */
  attempt: number;
}

/** Why the driver stopped short of committing the whole object. */
export type DriverFailure =
  | { kind: "session-gone" }
  | { kind: "client-error"; status: number }
  | { kind: "protocol-error"; detail: string }
  /**
   * Consecutive `308`s came back without a readable `Range`, so the run stopped
   * without the server's offset ever becoming legible.
   *
   * Distinct from `exhausted` on purpose: `exhausted` means the network or the
   * server kept failing, which a retry may well survive, while this one says
   * the responses arrived and could not be read. Whether that is the bucket's
   * expose policy, a proxy stripping the header, or simply a server at offset
   * zero is not decidable from the client — see {@link ChunkResponse}'s
   * `offset-unknown` member. Telling the two apart is still worth a label,
   * because they point an investigation at different places.
   */
  | { kind: "offset-unknown" }
  /**
   * The attempt budget ran out while operations kept failing or not
   * progressing.
   *
   * `lastReason` / `lastStatus` carry the last transport verdict out with it.
   * Without them a CORS-blocked PUT (`status 0`), a mobile timeout and five
   * consecutive 503s are one indistinguishable label — and "the request never
   * left the browser" versus "the server kept refusing" is the whole diagnosis.
   */
  | {
      kind: "exhausted";
      lastReason?: UploadTransportReason;
      lastStatus?: number;
    };

/** The driver's terminal outcome; abort surfaces as a thrown reason, not here. */
export type DriverOutcome = { kind: "complete" } | { kind: "failed"; failure: DriverFailure };

/** Everything {@link runResumableUpload} needs for one run. */
export interface RunResumableUploadParams {
  /** The session URI to PUT chunks against. */
  url: string;
  /** The recording bytes. */
  blob: Blob;
  /** `blob.size`, passed explicitly so the arithmetic is testable in isolation. */
  totalBytes: number;
  /**
   * Bytes per chunk PUT; the smallest legal upload is one chunk. Defaults to
   * `UPLOAD_CHUNK_SIZE_BYTES`.
   */
  chunkSizeBytes?: number;
  /**
   * Per-request timeout, forwarded to every {@link ChunkRequest}. Defaults to
   * `UPLOAD_CHUNK_TIMEOUT_MS`.
   */
  timeoutMs?: number;
  /**
   * Progress-idle stall window, forwarded to every {@link ChunkRequest} when
   * given. Omit it to let the transport apply the central default.
   */
  stallTimeoutMs?: number;
  /** The transport binding; defaults to `xhrTransport`, tests inject a fake. */
  transport?: ChunkTransport;
  /** The one cancellation channel for this run. */
  signal: AbortSignal;
  /** Injected clock for backoff; defaults to {@link abortableSleep}. */
  sleep?: SleepFn;
  /** Called whenever the phase or committed offset changes. */
  onPhase?: (update: DriverPhaseUpdate) => void;
  /**
   * Called with the run's monotonic display progress (0..1) as bytes flush.
   * The driver owns both the total and the display high-water clamp, so this
   * value never decreases and never exceeds 1 within a run.
   */
  onProgress?: (progress: number) => void;
  /**
   * The offset the server last acknowledged for this session, when the run
   * picks up a session that already holds bytes — a recording streamed while
   * it was made. Must come from a server response, never a local count: the
   * invariant above does not relax because the bytes arrived earlier.
   * Defaults to `0`.
   */
  initialOffset?: number;
  /**
   * Open with an offset probe instead of a write. For a session inherited in
   * an unknown state — its last request timed out or came back unreadable —
   * where the first thing worth knowing is what the server holds.
   */
  probeFirst?: boolean;
  /**
   * Called once for every operation that came back retryable, with the
   * transport's verdict, including the one that exhausts the attempt budget.
   * A telemetry seam: it lets the caller count retries and stalls without
   * widening {@link DriverOutcome}. It observes and never steers the loop.
   */
  onRetryable?: (reason: UploadTransportReason) => void;
}

/** Clamp `value` into the inclusive `[min, max]` range. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Drive one recording to a committed object, or to a terminal failure.
 *
 * Rejects (never resolves) if `signal` aborts, with the signal's reason — the
 * caller distinguishes that from a {@link DriverFailure} by the throw. Resolves
 * `{ kind: "complete" }` once the server acknowledges the whole object, or
 * `{ kind: "failed", failure }` on any terminal protocol outcome.
 *
 * @param params - see {@link RunResumableUploadParams}.
 */
export async function runResumableUpload(params: RunResumableUploadParams): Promise<DriverOutcome> {
  const {
    url,
    blob,
    totalBytes,
    chunkSizeBytes = UPLOAD_CHUNK_SIZE_BYTES,
    timeoutMs = UPLOAD_CHUNK_TIMEOUT_MS,
    stallTimeoutMs,
    transport = xhrTransport,
    signal,
    sleep = abortableSleep,
    onPhase,
    onProgress,
    onRetryable,
  } = params;

  let committed = params.initialOffset ?? 0; // server-acknowledged high-water offset. NEVER advanced locally.
  let attempts = 0; // consecutive failed ops WITHOUT forward progress
  let probeNext = params.probeFirst ?? false; // the next op re-establishes truth instead of writing
  let inFlightLoaded = 0; // bytes of the CURRENT op flushed so far
  let offsetUnknowns = 0; // CONSECUTIVE 308s whose Range could not be read
  // The last transport verdict, carried out with `exhausted`: a CORS-blocked
  // PUT (status 0), a mobile timeout and five 503s are different diagnoses.
  let lastReason: UploadTransportReason | undefined;
  let lastStatus: number | undefined;
  let displayHigh = 0; // monotonic display clamp

  /** Recompute and report display progress from `committed + inFlightLoaded`. */
  const report = (): void => {
    const raw = totalBytes === 0 ? 1 : clamp((committed + inFlightLoaded) / totalBytes, 0, 1);
    // `inFlightLoaded` resets to 0 between ops, so the raw value dips on a
    // retry; the bar must not jump backwards.
    displayHigh = Math.max(displayHigh, raw);
    onProgress?.(displayHigh);
  };
  /**
   * Zero the in-flight bytes and re-report: true as an operation begins and
   * again once it has answered.
   */
  const resetInFlight = (): void => {
    inFlightLoaded = 0;
    report();
  };
  /** The budget ran out; carries the last transport verdict out with it. */
  const exhausted = (): DriverOutcome => ({
    kind: "failed",
    failure: {
      kind: "exhausted",
      ...(lastReason === undefined ? {} : { lastReason }),
      ...(lastStatus === undefined ? {} : { lastStatus }),
    },
  });
  /** Back off before the next attempt; rejects at once on abort. */
  const backoff = (): Promise<void> => sleep(computeBackoffDelayMs(attempts), signal);

  while (committed < totalBytes) {
    throwIfAborted(signal);

    const isProbe = probeNext;
    const end = Math.min(committed + chunkSizeBytes, totalBytes); // exclusive
    const body = isProbe ? null : blob.slice(committed, end);
    const contentRange = isProbe
      ? `bytes */${totalBytes}`
      : // `end - 1` because Content-Range is INCLUSIVE on both bounds.
        `bytes ${committed}-${end - 1}/${totalBytes}`;

    onPhase?.({
      phase: isProbe ? "resuming" : "uploading",
      bytesCommitted: committed,
      totalBytes,
      attempt: attempts,
    });
    // A new operation begins: no in-flight bytes yet.
    resetInFlight();

    const res = await transport({
      url,
      body,
      contentRange,
      timeoutMs,
      ...(stallTimeoutMs === undefined ? {} : { stallTimeoutMs }),
      signal,
      onProgress: (loaded) => {
        inFlightLoaded = loaded;
        report();
      },
    });

    switch (res.kind) {
      case "complete": {
        committed = totalBytes;
        resetInFlight();
        return { kind: "complete" };
      }
      case "incomplete": {
        const n = res.nextOffset;
        if (!Number.isInteger(n) || n < 0 || n > totalBytes) {
          return {
            kind: "failed",
            failure: { kind: "protocol-error", detail: "offset out of range" },
          };
        }
        if (n < committed) {
          return {
            kind: "failed",
            failure: { kind: "protocol-error", detail: "offset regressed" },
          };
        }
        const progressed = n > committed;
        committed = n;
        probeNext = false;
        // A legible answer breaks the run of illegible ones.
        offsetUnknowns = 0;
        resetInFlight();
        if (progressed) {
          attempts = 0;
        } else {
          attempts += 1;
          if (attempts >= UPLOAD_CHUNK_MAX_ATTEMPTS) return exhausted();
          await backoff();
        }
        continue;
      }
      case "offset-unknown": {
        offsetUnknowns += 1;
        // The server answered 308 but its offset is not legible from here.
        // `committed` must NOT move back to zero: past the first chunk that
        // trips the `n < committed` guard in the `incomplete` branch and ends
        // the run as an offset-regressed protocol error, and at the first chunk
        // it re-sends byte 0 for the whole attempt budget. Holding `committed`
        // where the server last put it keeps the run recoverable — if any later
        // response carries a readable `Range`, the loop picks up from there.
        //
        // Re-driving the chunk, not probing: a probe against the same host
        // answers with the same unreadable header, so it would spend an attempt
        // to learn nothing.
        probeNext = false;
        resetInFlight();
        attempts += 1;
        // Two, not the full attempt budget. One re-drive is a real recovery —
        // if the server truly held zero bytes, re-sending the chunk is exactly
        // right. A second illegible answer says the header is not coming, and
        // every further attempt re-uploads a whole chunk body of a phone's data
        // to learn the same thing again.
        if (offsetUnknowns >= 2 || attempts >= UPLOAD_CHUNK_MAX_ATTEMPTS) {
          return { kind: "failed", failure: { kind: "offset-unknown" } };
        }
        await backoff();
        continue;
      }
      case "session-gone":
        return { kind: "failed", failure: { kind: "session-gone" } };
      case "client-error":
        return { kind: "failed", failure: { kind: "client-error", status: res.status } };
      case "protocol-error":
        return { kind: "failed", failure: { kind: "protocol-error", detail: res.detail } };
      case "retryable": {
        onRetryable?.(res.reason);
        offsetUnknowns = 0;
        lastReason = res.reason;
        lastStatus = res.status;
        attempts += 1;
        if (attempts >= UPLOAD_CHUNK_MAX_ATTEMPTS) return exhausted();
        await backoff();
        probeNext = true; // re-establish truth before writing again
        continue;
      }
    }
  }

  // Reachable only when the caller passes a zero-length blob; pre-flight rejects
  // that before the driver runs, but the loop must still terminate safely.
  return { kind: "complete" };
}
